import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Client } from '@hiveio/dhive';
import HIVE_RPC_NODES from '../../config/hiveNodes';
import { useSettings } from '../../context/SettingsContext';
import useBodyScrollLock from '../../utils/useBodyScrollLock';
import './VotersModal.css';

const client = new Client(HIVE_RPC_NODES);

const VotersModal = ({ author, permlink, onClose, votesWithRewards = [] }) => {
  const { rewardDisplayMode, rewardDisplayCurrency } = useSettings();
  const [voters, setVoters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [localMode, setLocalMode] = useState(rewardDisplayMode);

  useBodyScrollLock(true);

  const handleModeChange = (newMode) => {
    setLocalMode(newMode);
  };

  useEffect(() => {
    let cancelled = false;

    const fetchVoters = async () => {
      try {
        // If caller provided precomputed votesWithRewards, use them immediately
        if (votesWithRewards && votesWithRewards.length > 0) {
          setVoters(votesWithRewards.filter(v => v.percent !== 0).sort((a, b) => parseFloat(b.rshares) - parseFloat(a.rshares)));
          setLoading(false);
          return;
        }

        // Show loading indicator for remote fetch
        setLoading(true);

        const activeVotes = await client.database.call('get_active_votes', [author, permlink]);

        if (cancelled) return;

        // Filter out zero-weight votes, sort upvotes first then downvotes (by abs rshares)
        const allVotes = activeVotes
          .filter(v => v.percent !== 0)
          .sort((a, b) => parseFloat(b.rshares) - parseFloat(a.rshares));

        setVoters(allVotes);
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching voters:', err);
          setError('Failed to load voters');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchVoters();
    return () => { cancelled = true; };
  }, [author, permlink, votesWithRewards]);

  const formatVoteWeight = (percent) => {
    return (percent / 100).toFixed(2) + '%';
  };

  const formatRewardValue = (value, unit) => {
    return value ? value.toFixed(3) + ` ${unit}` : `0.000 ${unit}`;
  };

  const getDisplayReward = (reward) => {
    // If in author_only mode, show 50% (author's share is typically 50% of payout)
    if (localMode === 'author_only' && reward !== undefined) {
      return reward * 0.5;
    }
    return reward;
  };

  const rewardUnit = rewardDisplayCurrency === 'hbd' ? 'HBD' : 'HIVE';
  const getRewardValue = (voter) => {
    if (rewardDisplayCurrency === 'hbd') {
      return voter.reward_hbd !== undefined ? voter.reward_hbd : voter.reward_hive;
    }
    return voter.reward_hive !== undefined ? voter.reward_hive : voter.reward_hbd;
  };

  const modalContent = (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content voters-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            Voters ({voters.filter(v => v.percent > 0).length}
            {voters.some(v => v.percent < 0) && ` · ${voters.filter(v => v.percent < 0).length} downvote`})
          </h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-reward-mode">
          <label>Show rewards:</label>
          <select
            value={localMode}
            onChange={(e) => handleModeChange(e.target.value)}
            style={{ cursor: 'pointer', padding: '4px 8px', borderRadius: '4px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--text-primary)' }}
          >
            <option value="default">Default (Author + Curator)</option>
            <option value="author_only">Author Only</option>
          </select>
        </div>
        
        <div className="modal-body">
          {loading ? (
            <div className="loading-state">
              <svg className="spinner-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" opacity="0.25" />
                <path d="M12 2 A10 10 0 0 1 22 12" strokeLinecap="round" />
              </svg>
              <p>Loading voters...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
            </div>
          ) : voters.length === 0 ? (
            <div className="empty-state">
              <p>No votes yet</p>
            </div>
          ) : (
            <div className="voters-list">
              {voters.map((voter, index) => (
                <div key={voter.voter} className="voter-item">
                  <span className="voter-rank">{index + 1}</span>
                  <Link to={`/user/${voter.voter}`} className="voter-name" onClick={(e) => {
                    e.stopPropagation();
                  }}>
                    @{voter.voter}
                  </Link>
                  <span className={`vote-weight${voter.percent < 0 ? ' downvote' : ''}`}>{formatVoteWeight(voter.percent)}</span>
                  {voter.percent > 0 && getRewardValue(voter) !== undefined && (
                    <span className="vote-reward">
                      {formatRewardValue(getDisplayReward(getRewardValue(voter)), rewardUnit)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') {
    return modalContent;
  }

  return createPortal(modalContent, document.body);
};

export default VotersModal;
